import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { siteInfo, stats, testimonials } from "@/mocks/site";

const BASE = "https://danmeimh.net.cn";

const principles = [
  {
    icon: "ri-shield-check-line",
    title: "先核实，再发布",
    desc: "无法确认来源的信息不会进入正式栏目，宁可少发一条，也不把不确定当成确定。",
  },
  {
    icon: "ri-focus-3-line",
    title: "讲机制，不站队",
    desc: "我们更关心一件事为什么会发生，而不是替谁下判断，把判断权留给读者。",
  },
  {
    icon: "ri-refresh-line",
    title: "有增量才跟进",
    desc: "当话题没有新信息时，我们选择暂停，而不是换个标题把旧内容再发一遍。",
  },
  {
    icon: "ri-emotion-happy-line",
    title: "轻松但不轻率",
    desc: "语言可以活泼，事实必须严谨。吃瓜是入口，理解才是目的。",
  },
];

export default function About() {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于我们",
      url: `${BASE}/about`,
      description:
        "黑料每日是一个专注娱乐行业观察与资讯整理的日更内容站点，每天固定更新早报与一瓜。",
      inLanguage: "zh-CN",
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: siteInfo.name,
      url: `${BASE}/`,
      email: siteInfo.email,
      description: siteInfo.description,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "关于我们", item: `${BASE}/about` },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="关于我们 | 黑料每日的内容原则与团队"
        description="了解黑料每日的定位、发展理念与内容原则：每天固定更新早报与一瓜，专注娱乐行业观察，坚持先核实再发布、讲机制不站队。"
        keywords="黑料每日,关于我们,内容原则,娱乐行业观察,编辑团队"
        canonicalPath="/about"
        jsonLd={jsonLd}
      />
      <div className="px-4 md:px-8">
        <Breadcrumbs items={[{ label: "关于我们" }]} />
      </div>

      <section className="px-4 md:px-8 pb-10 border-b border-background-200" aria-labelledby="about-title">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium tracking-[0.2em] text-accent-700 uppercase mb-2">
              <span className="w-6 h-px bg-accent-500" />
              ABOUT US
            </span>
            <h1 id="about-title" className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
              一个把瓜讲明白的日更栏目
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              黑料每日诞生于 {siteInfo.since} 年，最初只是编辑组内部的一份每日整理。慢慢地，我们把它做成两档固定栏目：早上的《每日早报》和晚上的《每日一瓜》。
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              我们不追求最快，而是追求「读完确实知道发生了什么」。每一期都尽量给出可核查的公开信息、清晰的时间线和必要的背景，让读者用最少的时间获得最多的理解。
            </p>
          </div>
          <div className="aspect-[4/3] w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20editorial%20newsroom%20desk%20with%20stacked%20newspapers%2C%20open%20notebook%2C%20vintage%20camera%20and%20a%20cup%20of%20tea%2C%20cream%20walls%20and%20amber%20window%20light%2C%20crimson%20accent%20details%2C%20no%20people&width=1200&height=900&seq=hl-about-01&orientation=landscape"
              alt="黑料每日编辑工作台，桌面摆放着报纸、笔记本与相机"
              title="黑料每日编辑工作台 - 娱乐行业观察"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 py-12 md:py-16 bg-background-100 border-b border-background-200" aria-labelledby="stats-title">
        <h2 id="stats-title" className="sr-only">站点数据</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((item) => (
            <div key={item.label} className="text-center">
              <p className="font-heading text-3xl md:text-4xl font-bold text-primary-600">{item.value}</p>
              <p className="text-xs md:text-sm text-foreground-500 mt-2">{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-8 py-12 md:py-16" aria-labelledby="principle-title">
        <div className="mb-8">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium tracking-[0.2em] text-accent-700 uppercase mb-2">
            <span className="w-6 h-px bg-accent-500" />
            PRINCIPLES
          </span>
          <h2 id="principle-title" className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            我们的内容原则
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {principles.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="w-12 h-12 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 mb-4">
                <i className={`${item.icon} text-2xl`} aria-hidden="true" />
              </span>
              <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-8 py-12 md:py-16 bg-background-100 border-y border-background-200" aria-labelledby="voice-title">
        <div className="mb-8">
          <h2 id="voice-title" className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            用户口碑
          </h2>
          <p className="text-sm text-foreground-600 mt-2">来自不同职业读者的反馈，也是我们持续更新的理由。</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {testimonials.map((item) => (
            <figure key={item.name} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <i className="ri-double-quotes-l text-2xl text-accent-400" aria-hidden="true" />
              <blockquote className="text-sm text-foreground-700 leading-relaxed mt-3">{item.quote}</blockquote>
              <figcaption className="mt-4 text-sm font-medium text-foreground-950">
                {item.name}
                <span className="block text-[11px] text-foreground-500 font-normal">{item.role}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-8 py-12 md:py-16" aria-labelledby="contact-info-title">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <h2 id="contact-info-title" className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-6">
              选题建议、内容纠错、投稿报料与商务合作，都可以通过这些方式找到我们。
            </p>
            <dl className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 shrink-0">
                  <i className="ri-mail-line" aria-hidden="true" />
                </span>
                <div>
                  <dt className="text-xs text-foreground-500">合作咨询</dt>
                  <dd>
                    <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-900 hover:text-primary-600 transition-colors">
                      {siteInfo.email}
                    </a>
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-accent-100 text-accent-800 shrink-0">
                  <i className="ri-send-plane-line" aria-hidden="true" />
                </span>
                <div>
                  <dt className="text-xs text-foreground-500">报料投稿</dt>
                  <dd>
                    <a href={`mailto:${siteInfo.tipEmail}`} className="text-sm text-foreground-900 hover:text-primary-600 transition-colors">
                      {siteInfo.tipEmail}
                    </a>
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 shrink-0">
                  <i className="ri-time-line" aria-hidden="true" />
                </span>
                <div>
                  <dt className="text-xs text-foreground-500">更新节奏</dt>
                  <dd className="text-sm text-foreground-900">{siteInfo.updateTime}</dd>
                </div>
              </div>
            </dl>
          </div>
          <div className="rounded-lg border border-background-200 bg-secondary-900 p-6 md:p-8 flex flex-col justify-center">
            <h3 className="font-heading text-xl font-bold text-background-50 mb-3">想第一时间收到早报？</h3>
            <p className="text-sm text-secondary-100/85 leading-relaxed mb-5">
              留下你的联系方式，我们会在每期更新后提醒你。也可以直接给我们留言，谈谈你希望看到的内容。
            </p>
            <Link
              to="/contact"
              className="inline-flex w-fit items-center gap-2 rounded-md bg-primary-500 text-background-50 px-5 py-3 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-mail-send-line" aria-hidden="true" />
              前往联系我们
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}